import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ChangeDetectorRef, OnInit
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { iif, Observable, zip } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { environment as ENV } from '@environment';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import {
  PERSON_FORM_INPUTS, BILLING_FORM_INPUTS,
  PERSON_ADDRESS_FORM_INPUTS, ND, BOOKING_FORM_INPUTS
} from '@booking/constants/forms.constants';
import {
  BillingResponse, BillingInfo
} from '@billing/entities/billing.payload';
import { BillingPersonType } from '@booking/entities/billing-person-types';
import { BillingFormService } from '@billing/services/billing-form.service';
import { UserGender } from '@booking/entities/user-gender';
import { FormValidationService } from '@reservation/services/form-validation.service';
import {
  BookingBillingTypeDocumentProps, BookingBillingDocumentValidations
} from '@app/entities/booking-documents';
import { BookingDocumentBillingIdType } from '@app/entities/booking-billing-documents-id';
import { BillingDocumentsPipe } from '@billing/pipes/billing-documents.pipe';
import { disabledBillingCountrySelectorMapper } from '@reservation/mappers/country-selector-disabled.mapper';
import {
  billingFormDocumentType, billingFormDocumentValidators, defaultBillingDocumentByForm,
  defaultBillingDocumentId, getBillingDocumentNameById
} from '@forms/mappers/form-document-input.mapper';
import { BillingTypeFormAbstract } from '@billing/abstract/billing-type-form.abstract';
import { AssetBookingConfig } from '@asset-booking-config';

@Component({
  selector: 'app-company-billing-form',
  templateUrl: './company-billing-form.component.html',
  styleUrls: ['./company-billing-form.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompanyBillingFormComponent extends BillingTypeFormAbstract implements OnInit  {

  @Input() defaultBilling: BillingResponse;
  @Input() activeNaturalPerson: boolean;

  public companyForm: UntypedFormGroup;
  public type: BillingPersonType = BillingPersonType.JURIDICAL_PERSON;
  public emailSelector: string = PERSON_FORM_INPUTS.EMAIL;

  constructor(
    private changeDetector: ChangeDetectorRef,
    protected billingFormService: BillingFormService,
    protected billingDocumentsPipe: BillingDocumentsPipe,
    protected formValidationService: FormValidationService
  ) {
    super(billingFormService, billingDocumentsPipe, formValidationService);
  }

  ngOnInit(): void {
    this.setCompanyBillingForm();
    this.form?.addControl(BILLING_FORM_INPUTS.BILLING_INFO, new UntypedFormGroup(this.companyForm.controls));
  }

  get documents$(): Observable<BookingBillingTypeDocumentProps[]> {
    return this.documentsByType$(this.type, this.countryCodeByDocuments);
  }

  get documentType$(): Observable<BookingDocumentBillingIdType> {
    return this.documents$.pipe(map(documents => billingFormDocumentType(this.companyForm, documents)));
  }

  get defaultDocument$(): Observable<string> {
    return this.documents$.pipe(map(documents => defaultBillingDocumentByForm(documents, this.companyForm)));
  }

  get formDocumentValidations$(): Observable<BookingBillingDocumentValidations> {
    return this.documents$.pipe(map(documents => billingFormDocumentValidators(this.companyForm, documents)));
  }

  get hasDefaultBillingInfo(): boolean {
    return this.hasDefaultBillingInfoAbstract(this.defaultBilling);
  }

  get addressForm(): UntypedFormGroup {
    return this.addressFormAbstract(this.companyForm);
  }

  private get billingCompany(): BillingInfo {
    return this.companyForm ? this.companyForm.value as BillingInfo : null;
  }

  private get countryCodeByDocuments(): string {
    return this.billingCompany ? this.billingCompany.address?.country_code : this.defaultAddressFormData?.country_code;
  }

  public setCompanyBillingForm(): void {
    zip(
      this.documentType$,
      this.formDocumentValidations$,
      iif(
        () => !!this.defaultBilling,
        this.billingDocumentsPipe.transform(this.type, this.countryCodeByDocuments),
        this.billingDocumentsPipe.transform(this.type, ENV.countryInfo.iso_code),
      )
    ).pipe(
      take(1),
      tap(([documentType, validators, documents]) => {
        this.isSubmitted = false;
        this.defaultAddressFormData = this.defaultAddressFormDataAbstract(this.defaultBilling);
        this.selectedCityCode = this.defaultAddressFormData.city_code;
        this._country = this.defaultAddressFormData.country_code;
        this.createCompanyForm(documents);

        this.formValidationService.setBillingDocumentValidators(this.companyForm, documentType, validators);
      })
    ).subscribe().unsubscribe();
  }

  public onDocumentTypeSelected(event: OptionSelected): void {
    this.documents$.pipe(
      take(1),
      tap(documents => {
        this.companyForm.patchValue({ [PERSON_FORM_INPUTS.DOCUMENT_ID]: event.value });
        if (documents?.length) {
          this.disabledCountryOptions = disabledBillingCountrySelectorMapper(this.billingCompany, this.businessRules, documents);
        }
        this.changeDetector.detectChanges();
      })
    ).subscribe().unsubscribe();
  }

  public selectedCountry(country: string): void {
    if (this.documentSelectorRef) {
      zip(
        this.defaultDocument$, this.billingDocumentsPipe.transform(this.type, country)
      ).pipe(
        take(1), tap(([defaultDocumentInForm, documents]) => {
          const defaultFormDocumentId = defaultBillingDocumentId(documents, this.user, this.defaultBilling, null,
            this.hasDefaultBillingInfo, this.hasUserInfo, false);
          const defaultDocumentName = getBillingDocumentNameById(documents, defaultFormDocumentId);
          const tags = documents.map(doc => doc.DOCUMENT_NAME);
          this.documentSelectorRef.changeByCountry(defaultDocumentInForm, defaultDocumentName, tags);
          if (this._country !== country) {
            this._country = country;
            if (!isNullOrUndefined(this.selectedCityCode) && !isEmpty(this.selectedCityCode)) {
              this.selectedCityCode = '';
              this.addressForm.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]: '' });
            }
          }
        })
      ).subscribe().unsubscribe();
    }
  }

  public selectedCity(code: string): void {
    this.selectedCityAbstract(code, this.companyForm, this.addressForm);
  }

  public save(): void {
    this.isSubmitted = true;
    this.validateFileds();
    if (!this.billingFormService.formInvalidControls(this.companyForm)) {
      const billingInfo = {
        ...this.companyForm.value,
        [PERSON_FORM_INPUTS.EMAIL]: this.companyForm.controls[PERSON_FORM_INPUTS.EMAIL].value,
        [PERSON_FORM_INPUTS.DOCUMENT_ID]: this.companyForm.controls[PERSON_FORM_INPUTS.DOCUMENT_ID].value,
        [PERSON_FORM_INPUTS.DOCUMENT_NUMBER]: this.companyForm.controls[PERSON_FORM_INPUTS.DOCUMENT_NUMBER].value,
        [PERSON_FORM_INPUTS.ADDRESS]: { ...this.companyForm.controls[PERSON_FORM_INPUTS.ADDRESS].value }
      };
      if (this.hasDefaultBillingInfo) {
        this.update.emit({
          userId: this.defaultBilling.user_id,
          billingId: this.defaultBilling._id, billingInfo: {
            billing_info: billingInfo
          }
        });
      } else {
        this.create.emit({
          createForm: this.saveFormData, payload: {
            user_id: this.user.id,
            billing_info: billingInfo
          }
        });
      }
    }
  }

  private createCompanyForm(documents: BookingBillingTypeDocumentProps[]): void {
    const bookingFormValues = this.bookingForm.value;
    this.companyForm = new UntypedFormGroup({
      [PERSON_FORM_INPUTS.NATURAL_PERSON]: new UntypedFormControl(false),
      [PERSON_FORM_INPUTS.LEGAL_NAME]: new UntypedFormControl(
        this.hasDefaultBillingInfo ? this.defaultBilling.billing_info.legal_name : '',
        [Validators.minLength(2), Validators.maxLength(80), Validators.required]),
      [PERSON_FORM_INPUTS.FIRST_NAME]: new UntypedFormControl(ND, this.formValidationService.stringValidators),
      [PERSON_FORM_INPUTS.SECOND_NAME]: new UntypedFormControl(ND, this.formValidationService.secondNameValidators),
      [PERSON_FORM_INPUTS.LAST_NAME]: new UntypedFormControl(ND, this.formValidationService.stringValidators),
      [PERSON_FORM_INPUTS.GENDER]: new UntypedFormControl(
        UserGender.Male,
        this.formValidationService.stringValidators
      ),
      [PERSON_FORM_INPUTS.DOCUMENT_NUMBER]: new UntypedFormControl(
        this.hasDefaultBillingInfo ? this.defaultBilling.billing_info.document_number : ''),
      [PERSON_FORM_INPUTS.DOCUMENT_ID]: new UntypedFormControl(
        defaultBillingDocumentId(documents, this.user, this.defaultBilling, null,
          this.hasDefaultBillingInfo, this.hasUserInfo, false), [Validators.required]),
      [PERSON_FORM_INPUTS.PHONE]: new UntypedFormControl(
        this.hasDefaultBillingInfo ? this.defaultBilling.billing_info.phone_number :
          bookingFormValues[BOOKING_FORM_INPUTS.PHONE],
        [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[0-9]*$')]
      ),
      [PERSON_FORM_INPUTS.EMAIL]: new UntypedFormControl(
        this.hasDefaultBillingInfo ? this.defaultBilling.billing_info.email_address :
          bookingFormValues[BOOKING_FORM_INPUTS.EMAIL],
        [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.email]
      ),
      [PERSON_FORM_INPUTS.ADDRESS]: this.addressFormFields()
    });
  }

  private validateFileds(): void {
    let defaultRefs = {
      [PERSON_FORM_INPUTS.LEGAL_NAME]: this.legalNameRef,
      [PERSON_FORM_INPUTS.EMAIL]: this.emailRef,
      [PERSON_FORM_INPUTS.DOCUMENT_ID]: this.documentNumberRef,
      [PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE]: this.addressFormRef.countryRef,
      [PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]: this.addressFormRef.cityRef,
      [PERSON_ADDRESS_FORM_INPUTS.STREET]: this.addressFormRef.streetRef,
    };
    if (AssetBookingConfig.BILLING_MODULE.ENABLED_FULL_ADDRESS_FORM) {
      defaultRefs = {
        ...defaultRefs,
        [PERSON_ADDRESS_FORM_INPUTS.STATE_CODE]: this.addressFormRef.stateRef
      };
    }
    this.validateFiledsAbstract(this.companyForm, defaultRefs);
  }
}
